/**
 * Custom pointer-based drag for dragging a stock-panel item onto the poster canvas.
 *
 * Native HTML5 drag-and-drop can't give the UX we want (it paints its own ghost with the tile background,
 * shows a "copy" + badge, and forces the OS arrow cursor — CSS cursors are ignored mid-drag). So this drives
 * the drag with pointer events: a floating clone of the thumbnail follows the cursor, the body cursor is a
 * closed hand the whole time, and on release the whiteboard resolves whether the drop landed on the canvas.
 *
 * The drop target (whiteboard) registers a resolver here; the drag source (a grid/carousel tile) starts a
 * drag. Mirrors the `stock-media-popup-controller` singleton pattern.
 */

export interface StockDropPosition {
  left: number;
  top: number;
}

export type StockDropHandler = (point: StockDropPosition) => void;
export type StockDropResolver = (e: PointerEvent) => StockDropPosition | null;

export interface BeginStockItemDragOptions {
  startClientX: number;
  startClientY: number;
  sourceEl: HTMLElement;
  onDropOnCanvas: StockDropHandler;
  /**
   * Clone the whole tile as the drag ghost instead of just its inner `<img>`. Media tiles (image/video/sticker)
   * are essentially the image, so the bare-img ghost is right for them; audio tiles are rich cards (thumbnail +
   * title + duration + waveform), so they need the full element to look the same while dragging.
   */
  cloneFullElement?: boolean;
}

const DRAG_START_THRESHOLD = 5;
const CLONE_OPACITY = '0.85';
const CLONE_Z_INDEX = '2147483646';
/** Matches the audio card's `radius-12` corner so the full-element ghost is clipped to the same rounded shape. */
const CLONE_FULL_CORNER_RADIUS = '12px';
const GRABBING_CURSOR_STYLE_ID = 'pmw-stock-drag-cursor';

let dropResolver: StockDropResolver | null = null;

/** Forces the grabbing cursor everywhere for the duration of the drag — a global `!important` rule beats per-element cursors (canvas, buttons, etc.). */
const setGlobalGrabbingCursor = (): void => {
  if (document.getElementById(GRABBING_CURSOR_STYLE_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = GRABBING_CURSOR_STYLE_ID;
  style.textContent = '* { cursor: grabbing !important; }';
  document.head.appendChild(style);
};

const clearGlobalGrabbingCursor = (): void => {
  document.getElementById(GRABBING_CURSOR_STYLE_ID)?.remove();
};

/** Called by the whiteboard. Returns the poster-space point for a release over the canvas, else null. */
export function registerStockDropResolver(resolver: StockDropResolver): () => void {
  dropResolver = resolver;
  return (): void => {
    if (dropResolver === resolver) {
      dropResolver = null;
    }
  };
}

const createDragClone = (sourceEl: HTMLElement, rect: DOMRect, cloneFullElement: boolean): HTMLElement => {
  // data-stock-drag-thumb marks the tile's designated thumb element (e.g. the shape tile's recolored <img>).
  const thumb = sourceEl.querySelector<HTMLElement>('img, [data-stock-drag-thumb]');
  const source = cloneFullElement ? sourceEl : (thumb ?? sourceEl);
  const clone = source.cloneNode(true) as HTMLElement;
  clone.removeAttribute('draggable');
  clone.style.height = `${rect.height}px`;
  clone.style.left = `${rect.left}px`;
  clone.style.margin = '0';
  clone.style.opacity = CLONE_OPACITY;
  clone.style.pointerEvents = 'none';
  clone.style.position = 'fixed';
  clone.style.top = `${rect.top}px`;
  clone.style.width = `${rect.width}px`;
  clone.style.zIndex = CLONE_Z_INDEX;
  if (cloneFullElement) {
    // The source tile is an `<li>`; cloned out of its `<ul>` it regains the list-marker bullet and carries its
    // own square tile background. Strip the marker, drop the tile background, and clip to the card's rounded
    // corners so only the rounded card shows.
    clone.style.background = 'transparent';
    clone.style.borderRadius = CLONE_FULL_CORNER_RADIUS;
    clone.style.listStyle = 'none';
    clone.style.overflow = 'hidden';
  } else {
    clone.style.objectFit = 'contain';
  }
  return clone;
};

const suppressNextClick = (): void => {
  const block = (ev: MouseEvent): void => {
    ev.preventDefault();
    ev.stopPropagation();
  };
  document.addEventListener('click', block, {capture: true, once: true});
  window.setTimeout(() => {
    document.removeEventListener('click', block, true);
  }, 0);
};

/**
 * Starts tracking a potential drag from a stock tile. The drag only "begins" (clone + grabbing cursor) once
 * the pointer moves past a small threshold, so a plain click still adds the item the normal way.
 */
export function beginStockItemDrag({startClientX, startClientY, sourceEl, onDropOnCanvas, cloneFullElement = false}: BeginStockItemDragOptions): void {
  const rect = sourceEl.getBoundingClientRect();
  const offsetX = startClientX - rect.left;
  const offsetY = startClientY - rect.top;
  let started = false;
  let clone: HTMLElement | null = null;

  const positionClone = (x: number, y: number): void => {
    if (clone) {
      clone.style.left = `${x - offsetX}px`;
      clone.style.top = `${y - offsetY}px`;
    }
  };

  const cleanup = (): void => {
    document.removeEventListener('pointermove', onMove);
    document.removeEventListener('pointerup', onUp);
    document.removeEventListener('pointercancel', cleanup);
    if (clone) {
      clone.remove();
      clone = null;
    }
    clearGlobalGrabbingCursor();
    document.body.style.userSelect = '';
  };

  const onMove = (e: PointerEvent): void => {
    if (!started) {
      if (Math.abs(e.clientX - startClientX) < DRAG_START_THRESHOLD && Math.abs(e.clientY - startClientY) < DRAG_START_THRESHOLD) {
        return;
      }
      started = true;
      clone = createDragClone(sourceEl, rect, cloneFullElement);
      document.body.appendChild(clone);
      setGlobalGrabbingCursor();
      document.body.style.userSelect = 'none';
    }
    positionClone(e.clientX, e.clientY);
  };

  const onUp = (e: PointerEvent): void => {
    const wasDragging = started;
    cleanup();
    if (!wasDragging) {
      return;
    }
    suppressNextClick();
    const point = dropResolver ? dropResolver(e) : null;
    if (point) {
      onDropOnCanvas(point);
    }
  };

  document.addEventListener('pointermove', onMove);
  document.addEventListener('pointerup', onUp);
  document.addEventListener('pointercancel', cleanup);
}
